import api from '@/api/api';
import { toast } from 'react-toastify';

export const getParagraphs = async () => {
  try {
    const response = await api.get('/paragraphs');
    return response.data;
  } catch (error: any) {
    if (error?.response?.status !== 404) {
      if (error?.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error(`Error loading paragraphs.`);
      }
    }
    throw error;
  }
};

export const getOrps = async () => {
  try {
    const response = await api.get('/orps');
    const data = response.data;
    // Pokud API vrací objekt s číselnými klíči, převeď na pole
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      return Object.values(data);
    }
    return data;
  } catch (error: any) {
    if (error?.response?.status !== 404) {
      if (error?.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error(`Error loading paragraphs.`);
      }
    }
    throw error;
  }
};

export type ReportResult =
  | { kind: 'blob' }
  | { kind: 'queued'; jobId: string; message: string };

/**
 * Generate an xlsx report. The backend is polymorphic:
 *   - sync (≤ threshold ORPs): streams the xlsx; we trigger a download.
 *   - async (> threshold ORPs): returns 202 with a job_id; the report is
 *     emailed when ready. Caller must pass `email` for this branch.
 *
 * Returns ReportResult so the caller can show appropriate UI.
 * Throws on unrecoverable error; 409 (already running) is surfaced as a
 * toast and re-thrown.
 */
export const downloadReport = async (
  orps: string[],
  cat: string[],
  intro: boolean = false,
  email?: string
): Promise<ReportResult> => {
  const orpsArray = Array.isArray(orps) ? orps : Object.values(orps);
  const catArray = Array.isArray(cat) ? cat : Object.values(cat);

  try {
    const response = await api.post(
      '/xlsx-report-all-paragraphs',
      {
        // biome-ignore lint/style/useNamingConvention: <explanation>
        icos_or_orps: orpsArray,
        categories: catArray,
        // biome-ignore lint/style/useNamingConvention: <explanation>
        include_introduction: intro,
        // biome-ignore lint/style/useNamingConvention: <explanation>
        email_to: email,
      },
      {
        // Accept either a binary xlsx (sync) or a JSON envelope (async).
        // We get the raw blob and decide based on Content-Type.
        responseType: 'blob',
      }
    );

    const contentType: string = response.headers['content-type'] || '';

    if (contentType.includes('application/json')) {
      // Async branch — parse blob as text → JSON.
      const text = await (response.data as Blob).text();
      const body = JSON.parse(text);
      return { kind: 'queued', jobId: body.job_id, message: body.message };
    }

    // Sync branch — trigger a download.
    const contentDisposition = response.headers['content-disposition'];
    const filenameMatch = contentDisposition && contentDisposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch ? filenameMatch[1] : `report_orps.xlsx`;

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    return { kind: 'blob' };
  } catch (error: any) {
    // Error envelope is itself a Blob because of responseType: 'blob'.
    let detail: string | undefined;
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (data instanceof Blob) {
      try {
        detail = JSON.parse(await data.text())?.detail;
      } catch {
        detail = undefined;
      }
    } else {
      detail = data?.detail;
    }

    if (status === 409) {
      toast.warning(detail || 'Generování již probíhá. Vyčkejte prosím několik minut.');
    } else if (detail) {
      toast.error(`Error: ${detail}`);
    } else {
      toast.error(`Error downloading report file.`);
    }
    throw error;
  }
};

export const getContexts = async () => {
  try {
    const response = await api.get('/contexts');
    return response.data;
  } catch (error: any) {
    if (error?.response?.status !== 404) {
      if (error?.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error(`Error loading paragraphs.`);
      }
    }
    throw error;
  }
};
